import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ReceiptText, ChevronDown, ChevronUp, Pencil, PlusCircle, Loader2 } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FEE_OPTIONS = [
  ["delivery", "Delivery Fee"], ["airport", "Airport Delivery Fee"], ["cleaning", "Cleaning Charge"],
  ["fuel", "Fuel Charge"], ["late_fee", "Late Return Fee"], ["custom", "Custom Charge"],
];

export function BookingPricing({ booking, token, onSaved }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {key,label,amount}
  const [form, setForm] = useState({ amount: "", reason: "", note: "" });
  const [feeForm, setFeeForm] = useState(null); // {key,label,amount,reason}
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/bookings/${booking.id}/pricing`, auth);
      setData(d);
    } catch { toast.error("Could not load pricing breakdown."); }
    finally { setLoading(false); }
  };

  const toggle = () => { const next = !open; setOpen(next); if (next && !data) load(); };

  const saveOverride = async () => {
    if (!form.reason.trim()) return toast.error("A reason is required for every override.");
    setBusy(true);
    try {
      await axios.post(`${API}/bookings/${booking.id}/pricing/override`,
        { key: editing.key, new_amount: Number(form.amount) || 0, reason: form.reason, note: form.note }, auth);
      toast.success(`${editing.label} updated.`);
      setEditing(null); setForm({ amount: "", reason: "", note: "" });
      await load(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Override failed."); }
    finally { setBusy(false); }
  };

  const addFee = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/bookings/${booking.id}/pricing/add-fee`, {
        key: feeForm.key, amount: feeForm.amount === "" ? null : Number(feeForm.amount),
        label: feeForm.label || null, reason: feeForm.reason || "",
      }, auth);
      toast.success("Fee added to the booking.");
      setFeeForm(null);
      await load(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add fee."); }
    finally { setBusy(false); }
  };

  const snap = data?.pricing;
  const inputCls = "w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-sm text-white";

  return (
    <div className="mt-3">
      <button data-testid={`pricing-toggle-${booking.id}`} onClick={toggle}
        className="flex items-center gap-2 text-sm border border-white/15 text-white/80 px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors">
        <ReceiptText size={15} className="text-[#F2B705]" /> Pricing Breakdown {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="mt-3 bg-[#0C0C0C] border border-white/10 rounded-xl p-4" data-testid={`pricing-panel-${booking.id}`}>
          {loading && <div className="flex items-center gap-2 text-white/50 py-4 justify-center"><Loader2 className="animate-spin" size={15} /> Loading…</div>}
          {snap && !loading && (
            <>
              {data.reconstructed && <p className="text-white/35 text-xs mb-2">Booked before the pricing engine — this is a reconstructed estimate at today's rates. Overriding a line converts it to a live itemized breakdown.</p>}
              <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid={`coverage-switch-${booking.id}`}>
                <span className="text-white/35 text-[10px] uppercase tracking-wider mr-1">Insurance:</span>
                {[["own", "BYOI · $0/day"], ["rental", "All-In"], ["decline", "Declined + Waiver"]].map(([k, l]) => {
                  const active = (booking.coverage_selection || (booking.insurance_type === "AllIn" ? "rental" : "own")) === k;
                  return (
                    <button key={k} data-testid={`coverage-set-${k}-${booking.id}`} disabled={busy || active}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await axios.post(`${API}/bookings/${booking.id}/coverage`, { coverage_selection: k }, auth);
                          toast.success(`Insurance changed to ${l} — pricing recalculated.`);
                          await load(); onSaved?.();
                        } catch (e) { toast.error(e.response?.data?.detail || "Could not change coverage."); }
                        finally { setBusy(false); }
                      }}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${active
                        ? "border-[#F2B705] bg-[#F2B705]/15 text-[#F2B705] font-bold"
                        : "border-white/15 text-white/50 hover:text-white hover:border-white/40"}`}>
                      {l}
                    </button>
                  );
                })}
              </div>
              <div className="space-y-1">
                {snap.line_items.filter((i) => i.selected).map((i) => (
                  <div key={i.key} className="flex items-center gap-2 text-sm" data-testid={`admin-price-line-${i.key}`}>
                    <span className="text-white/75 min-w-0 truncate">{i.label}
                      {i.unit === "day" && i.amount > 0 && <span className="text-white/30 text-xs ml-1.5">{money(i.rate)}/day × {i.qty}</span>}
                      {i.overridden && <span className="text-[#F2B705] text-[10px] ml-1.5 uppercase font-bold" title="Overridden by management">adjusted</span>}
                    </span>
                    <span className="ml-auto text-white font-semibold shrink-0">{money(i.amount)}</span>
                    <button data-testid={`price-override-${i.key}`} title="Override this amount"
                      onClick={() => { setEditing(i); setForm({ amount: String(i.amount), reason: "", note: "" }); }}
                      className="text-white/30 hover:text-[#F2B705]"><Pencil size={13} /></button>
                  </div>
                ))}
                {Number(snap.tax) > 0 && <div className="flex justify-between text-sm text-white/70"><span>Taxes</span><span>{money(snap.tax)}</span></div>}
                {Number(data.discount) > 0 && <div className="flex justify-between text-sm text-emerald-300"><span>Discounts / Credits</span><span>-{money(data.discount)}</span></div>}
                <div className="flex justify-between border-t border-white/15 pt-2 mt-1.5">
                  <span className="text-white font-bold text-sm">Booking Total</span>
                  <span className="font-display font-black text-lg text-gold-gradient" data-testid={`pricing-total-${booking.id}`}>{money(Math.max(0, snap.total - (data.discount || 0)))}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-3">
                {FEE_OPTIONS.map(([k, l]) => (
                  <button key={k} data-testid={`add-fee-${k}-${booking.id}`}
                    onClick={() => setFeeForm({ key: k, label: k === "custom" ? "" : l, amount: "", reason: "" })}
                    className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/55 hover:text-[#F2B705] hover:border-[#F2B705]/40">
                    <PlusCircle size={11} /> {l}
                  </button>
                ))}
              </div>

              {(data.overrides || []).length > 0 && (
                <div className="mt-3 border-t border-white/10 pt-2">
                  <div className="text-white/35 text-[10px] uppercase tracking-wider mb-1">Override History</div>
                  {data.overrides.map((o) => (
                    <div key={o.id} className="text-xs text-white/45">
                      {o.label}: {o.action === "add_fee" ? "added" : `${money(o.original_amount)} → ${money(o.new_amount)}`}
                      {o.reason && ` — "${o.reason}"`} · {o.approved_by} · {fmtDateTime(o.at)}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {editing && (
            <div className="mt-3 bg-black/40 border border-[#F2B705]/30 rounded-lg p-3 space-y-2" data-testid="override-form">
              <div className="text-white text-sm font-semibold">Override: {editing.label} <span className="text-white/40 text-xs">(currently {money(editing.amount)})</span></div>
              <input data-testid="override-amount" type="number" step="0.01" min="0" placeholder="New amount $" value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className={inputCls} />
              <select data-testid="override-reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-sm text-white">
                <option value="">Reason (required — recorded in the audit log)</option>
                {["Returning Customer", "Long-Term Rental", "Family Rental", "Promotional Rate", "Negotiated Rate",
                  "Vehicle Utilization Special", "Customer Service Credit", "Management Courtesy", "Other"].map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <input data-testid="override-note" placeholder="Internal note (optional)" value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className={inputCls} />
              <div className="flex gap-2">
                <button data-testid="override-save" onClick={saveOverride} disabled={busy}
                  className="px-4 py-1.5 rounded-md bg-[#F2B705] text-black font-bold text-xs disabled:opacity-50">{busy ? "Saving…" : "Apply Override"}</button>
                <button onClick={() => setEditing(null)} className="px-3 py-1.5 rounded-md border border-white/15 text-white/60 text-xs">Cancel</button>
              </div>
            </div>
          )}

          {feeForm && (
            <div className="mt-3 bg-black/40 border border-white/20 rounded-lg p-3 space-y-2" data-testid="add-fee-form">
              <div className="text-white text-sm font-semibold">Add {feeForm.key === "custom" ? "Custom Charge" : feeForm.label}</div>
              {feeForm.key === "custom" && (
                <input data-testid="fee-label" placeholder="Charge label" value={feeForm.label}
                  onChange={(e) => setFeeForm((f) => ({ ...f, label: e.target.value }))} className={inputCls} />
              )}
              <input data-testid="fee-amount" type="number" step="0.01" min="0"
                placeholder={feeForm.key === "custom" || feeForm.key === "late_fee" ? "Amount $" : "Amount $ (blank = use Pricing Settings default)"}
                value={feeForm.amount} onChange={(e) => setFeeForm((f) => ({ ...f, amount: e.target.value }))} className={inputCls} />
              <input data-testid="fee-reason" placeholder="Reason / note (optional)" value={feeForm.reason}
                onChange={(e) => setFeeForm((f) => ({ ...f, reason: e.target.value }))} className={inputCls} />
              <div className="flex gap-2">
                <button data-testid="fee-save" onClick={addFee} disabled={busy}
                  className="px-4 py-1.5 rounded-md bg-[#F2B705] text-black font-bold text-xs disabled:opacity-50">{busy ? "Adding…" : "Add Charge"}</button>
                <button onClick={() => setFeeForm(null)} className="px-3 py-1.5 rounded-md border border-white/15 text-white/60 text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
